// src/components/store/sections/hero-banner/HeroSliderClassic.tsx
"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronLeft, ChevronRight, Sparkles, ArrowRight, ShoppingBag, Star } from "lucide-react";

export interface HeroBannerItem {
  id: number;
  badge_title?: string;
  title: string;
  subtitle?: string;
  button_text?: string;
  target_url?: string;
  image?: string | null;
  font_family?: string;
  title_color?: string;
  subtitle_color?: string;
  bg_color?: string;
  gradient_color_1?: string;
  gradient_color_2?: string;
  gradient_color_3?: string;
  gradient_direction?: string;
  show_avatar_badge?: boolean;
  avatar_badge_title?: string;
  avatar_rating?: string;
}

const DEFAULT_AVATARS = [
  "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=faces",
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=faces",
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=faces",
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=faces",
];

export default function HeroSliderClassic({
  banners = [],
  primaryColor = "#059669",
}: {
  banners?: HeroBannerItem[];
  primaryColor?: string;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? banners.length - 1 : prev - 1));
  }, [banners.length]);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev === banners.length - 1 ? 0 : prev + 1));
  }, [banners.length]);

  // 🌟 ১ মিনিট (৬০,০০০ মিলি-সেকেন্ড) পর পর অটোমেটিক স্লাইড পরিবর্তনের টাইমার
  useEffect(() => {
    if (!banners || banners.length <= 1 || isHovered) return;

    const timer = setInterval(() => {
      nextSlide();
    }, 60000); // ৬০ সেকেন্ড = ১ মিনিট

    return () => clearInterval(timer);
  }, [banners, isHovered, nextSlide]);

  const getBannerBackgroundStyle = (banner?: HeroBannerItem) => {
    if (!banner) return {};
    const dir = banner.gradient_direction || "to bottom right";
    const c1 = banner.gradient_color_1;
    const c2 = banner.gradient_color_2;
    const c3 = banner.gradient_color_3;

    if (c1 && c2 && c3) {
      return { background: `linear-gradient(${dir}, ${c1}, ${c2}, ${c3})` };
    }
    if (c1 && c2) {
      return { background: `linear-gradient(${dir}, ${c1}, ${c2})` };
    }
    if (banner.bg_color) {
      return { backgroundColor: banner.bg_color };
    }
    return { background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)" };
  };

  if (!banners || banners.length === 0) {
    return null;
  }

  const current = banners[currentIndex] || banners[0];
  const imageUrl = current.image
    ? current.image.startsWith("http")
      ? current.image
      : `http://127.0.0.1:8000${current.image.startsWith("/") ? "" : "/"}${current.image}`
    : null;

  const bannerFont = current.font_family || "Inter, sans-serif";

  return (
    <section 
      className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 relative group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        href="https://fonts.googleapis.com/css2?family=Anek+Bangla:wght@400;600;800&family=Bebas+Neue&family=Black+Ops+One&family=Cinzel:wght@600;800&family=Galada&family=Hind+Siliguri:wght@400;600;700&family=Inter:wght@400;700;900&family=Mina:wght@400;700&family=Montserrat:wght@400;700;900&family=Noto+Sans+Bengali:wght@400;700&family=Oswald:wght@500;700&family=Playfair+Display:wght@600;800&family=Plus+Jakarta+Sans:wght@600;800&family=Poppins:wght@400;600;800&family=Roboto:wght@400;700&family=Syne:wght@700;800&display=swap"
        rel="stylesheet"
      />

      {/* ব্যানার কার্ড ফ্রেম */}
      <div
        style={getBannerBackgroundStyle(current)}
        className="relative rounded-[32px] overflow-hidden p-8 sm:p-14 transition-all duration-700 min-h-[420px] sm:min-h-[460px] flex items-center shadow-2xl border border-slate-800"
      >
        {/* 🌟 কি চেঞ্জ হলে মসৃণ ফেড-ইন ও স্লাইড অ্যানিমেশন ট্রিগার হবে */}
        <div 
          key={currentIndex} 
          className="flex flex-col md:flex-row items-center justify-between gap-8 w-full z-10 animate-in fade-in zoom-in-95 duration-700"
        >
          {/* টেক্সট কনটেন্ট */}
          <div className="space-y-5 max-w-xl">
            <span
              style={{ borderColor: `${primaryColor}40`, color: primaryColor }}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border text-xs font-black uppercase tracking-wider"
            >
              <Sparkles className="w-3.5 h-3.5 animate-pulse" />
              {current.badge_title || "Special Collection"}
            </span>

            <h1
              style={{
                fontFamily: bannerFont,
                color: current.title_color || "#ffffff",
              }}
              className="text-3xl sm:text-5xl font-black tracking-tight leading-[1.15] drop-shadow-sm"
            >
              {current.title}
            </h1>

            <p
              style={{
                fontFamily: bannerFont,
                color: current.subtitle_color || "#cbd5e1",
              }}
              className="text-sm sm:text-base leading-relaxed text-slate-300"
            >
              {current.subtitle || "সেরা মানের প্রিমিয়াম প্রডাক্ট উপভোগ করুন নির্ভরযোগ্য সেবার সাথে।"}
            </p>

            <div className="pt-2">
              <Link
                href={current.target_url || "/products"}
                style={{ backgroundColor: primaryColor }}
                className="px-8 py-3.5 rounded-full text-slate-950 font-black text-xs inline-flex items-center gap-2 shadow-lg hover:opacity-95 transition-all hover:gap-3 cursor-pointer"
              >
                <span>{current.button_text || "Shop Now"}</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* প্রোডাক্ট ইমেজ ও কাস্টমার ব্যাজ */}
          <div className="relative w-64 h-64 sm:w-80 sm:h-80 flex items-center justify-center flex-shrink-0 bg-transparent">
            {imageUrl ? (
              <Image
                src={imageUrl}
                alt={current.title}
                fill
                unoptimized
                priority
                className="object-contain drop-shadow-2xl hover:scale-105 transition-transform duration-700"
              />
            ) : (
              <div className="flex flex-col items-center gap-2 text-slate-500">
                <ShoppingBag className="w-16 h-16 text-slate-600" />
                <span className="text-xs font-semibold">Premium Product Showcase</span>
              </div>
            )}

            {/* ফ্লোটিং কাস্টমার ব্যাজ */}
            {(current.show_avatar_badge ?? true) && (
              <div className="absolute -bottom-2 left-2 right-2 p-2 rounded-xl bg-slate-950/85 backdrop-blur-md border border-white/10 flex items-center justify-between shadow-lg">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2 overflow-hidden p-0.5">
                    {DEFAULT_AVATARS.map((src, i) => (
                      <div
                        key={i}
                        className="relative w-6 h-6 rounded-full border border-slate-900 overflow-hidden"
                      >
                        <Image
                          src={src}
                          alt="Customer"
                          fill
                          unoptimized
                          className="object-cover"
                        />
                      </div>
                    ))}
                  </div>
                  <div>
                    <h4 className="text-white text-[10px] font-bold leading-tight truncate max-w-[90px] sm:max-w-[110px]">
                      {current.avatar_badge_title || "15k+ Happy Customers"}
                    </h4>
                    <p className="text-[9px] text-slate-400">Verified</p>
                  </div>
                </div>

                <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[10px] font-black">
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                  <span>{current.avatar_rating || "4.9"}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 🌟 বাম পাশের অ্যারো বাটন (Left Arrow) */}
        {banners.length > 1 && (
          <button
            onClick={prevSlide}
            type="button"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-white/90 hover:bg-white text-slate-900 shadow-2xl border border-slate-200 flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 cursor-pointer backdrop-blur-xs"
            aria-label="Previous Slide"
          >
            <ChevronLeft className="w-6 h-6 stroke-[2.5]" />
          </button>
        )}

        {/* 🌟 ডান পাশের অ্যারো বাটন (Right Arrow) */}
        {banners.length > 1 && (
          <button
            onClick={nextSlide}
            type="button"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-white/90 hover:bg-white text-slate-900 shadow-2xl border border-slate-200 flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 cursor-pointer backdrop-blur-xs"
            aria-label="Next Slide"
          >
            <ChevronRight className="w-6 h-6 stroke-[2.5]" />
          </button>
        )}

        {/* 🌟 নিচে ডট ইন্ডিকেটর ও ১ মিনিটের প্রগ্রেস বার */}
        {banners.length > 1 && (
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-2">
            <div className="flex items-center gap-2 bg-slate-950/60 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10">
              {banners.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setCurrentIndex(idx)}
                  className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                    idx === currentIndex
                      ? "w-8 bg-emerald-400"
                      : "w-2 bg-white/40 hover:bg-white/80"
                  }`}
                  aria-label={`Slide ${idx + 1}`}
                />
              ))}
            </div>

            {/* ১ মিনিটের স্মুথ প্রগ্রেস বার */}
            {!isHovered && (
              <div className="w-24 h-0.5 bg-white/20 rounded-full overflow-hidden">
                <div
                  key={currentIndex}
                  className="h-full bg-emerald-400 rounded-full animate-[progress_60s_linear]"
                  style={{
                    animation: "progress 60s linear forwards",
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ১ মিনিটের CSS Keyframe Animation */}
      <style jsx>{`
        @keyframes progress {
          from {
            width: 0%;
          }
          to {
            width: 100%;
          }
        }
      `}</style>
    </section>
  );
}